<template>
  <DxPopup
    width="100vw"
    height="100vh"
    :show-close-button="false"
    :visible="visible"
    :resize-enabled="false"
    :drag-enabled="false"
    :wrapper-attr="popupAttributes"
    @hidden="onHidden"
  >
    <template #title>
      <div class="machine-split-header">
        <div class="machine-split-back" @click="onCancel">
          <img src="@/assets/images/icons/arrow-back.svg" />
          <span>{{ $t("common.back") }}</span>
        </div>
        <div class="machine-split-title">{{ $t("stock_mobile.form.split_quantity_by_machine") }}</div>
      </div>
    </template>
    <template #content>
      <div class="machine-split-content">
        <DxScrollView class="machine-split-scrollview" :use-native="true">
          <div class="machine-split-products">
            <div v-for="(product, pIdx) in productRows" :key="product.product_uuid" class="machine-split-product">
              <div class="machine-split-product-header">
                <span class="machine-split-product-name">{{ product.product_name }}</span>
                <span class="machine-split-product-total">{{ formatQty(product.total_quantity) }} {{ product.measure_unit_symbol }}</span>
              </div>
              <div
                class="machine-split-sum-indicator"
                :class="{ 'machine-split-sum-ok': isProductSumOk(product), 'machine-split-sum-error': !isProductSumOk(product) }"
              >
                {{ $t("stock_mobile.form.distributed") }}: {{ formatQty(productDistributed(product)) }} {{ product.measure_unit_symbol }}
              </div>
              <div
                v-for="(machineRow, mIdx) in product.machine_rows"
                :key="machineRow.work_machine_uuid"
                class="machine-split-machine-row"
              >
                <span class="machine-split-machine-name">{{ machineRow.machine_name }}</span>
                <DxNumberBox
                  :value="machineRow.quantity"
                  :min="0"
                  :width="90"
                  :show-spin-buttons="false"
                  :show-clear-button="false"
                  :format="numberFormat"
                  styling-mode="outlined"
                  @value-changed="onQtyChanged(pIdx, mIdx, $event)"
                />
                <span class="machine-split-machine-unit">{{ product.measure_unit_symbol }}</span>
              </div>
              <div class="dialog_title_separator"></div>
            </div>
          </div>
          <div class="machine-split-bottom-spacer"></div>
        </DxScrollView>
        <div v-if="globalError" class="machine-split-global-error">{{ globalError }}</div>
        <div class="machine-split-actions">
          <div class="sticky_action_button" :class="{ 'machine-split-btn-disabled': !allSumsOk }" @click="onConfirm">
            {{ $t("stock_mobile.form.confirm_and_save") }}
          </div>
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxScrollView } from "devextreme-vue/scroll-view";
import { DxNumberBox } from "devextreme-vue/number-box";
import { mapGetters } from "vuex";
import { getQuantityFormatter } from "@/helpers/stockHelper";

export default {
  name: "MachineConsumSplitPopup",
  components: {
    DxPopup,
    DxScrollView,
    DxNumberBox,
  },
  data() {
    return {
      visible: false,
      productRows: [],
      globalError: null,
      promiseResolve: null,
      popupAttributes: {
        class: "product_history_dialog_wrapper machine-split-popup-wrapper",
      },
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
    }),
    numberFormat() {
      return getQuantityFormatter();
    },
    allSumsOk() {
      return this.productRows.every((product) => this.isProductSumOk(product));
    },
  },
  methods: {
    /**
     * Open the popup with products and machines, defaulting to an equal quantity split.
     * @param {Array} products - consumData.products entries { uuid, name, quantity, measure_unit }
     * @param {Array} machines - selected machines { uuid, name }
     * @returns {Promise<Array|null>} resolves with [{ product_uuid, machine_splits: [{ work_machine_uuid, quantity }] }] or null on cancel
     */
    open(products = [], machines = []) {
      return new Promise((resolve) => {
        this.productRows = products.map((product) => this.buildProductRow(product, machines));
        this.globalError = null;
        this.promiseResolve = resolve;
        this.visible = true;
      });
    },
    buildProductRow(product, machines) {
      const totalQuantity = parseFloat(product.quantity) || 0;
      const equalShare = machines.length > 0 ? this.roundQty(totalQuantity / machines.length) : 0;

      let dividedQuantity = 0;
      const machine_rows = machines.map((machine, idx) => {
        const isLast = idx === machines.length - 1;
        const quantity = isLast ? this.roundQty(totalQuantity - dividedQuantity) : equalShare;
        if (!isLast) {
          dividedQuantity += quantity;
        }
        return {
          work_machine_uuid: machine.uuid,
          machine_name: machine.name,
          quantity,
        };
      });

      return {
        product_uuid: product.uuid,
        product_name: product.name,
        measure_unit_symbol: product.measure_unit?.symbol || product.measure_unit_symbol || "",
        total_quantity: totalQuantity,
        machine_rows,
      };
    },
    roundQty(value) {
      return parseFloat((parseFloat(value) || 0).toFixed(this.quantityDecimalPlaces));
    },
    formatQty(value) {
      return parseFloat(parseFloat(value || 0).toFixed(this.quantityDecimalPlaces));
    },
    productDistributed(product) {
      return product.machine_rows.reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0);
    },
    isProductSumOk(product) {
      const diff = Math.abs(
        parseFloat(this.productDistributed(product).toFixed(this.quantityDecimalPlaces)) -
          parseFloat(product.total_quantity.toFixed(this.quantityDecimalPlaces))
      );
      return diff < Math.pow(10, -this.quantityDecimalPlaces);
    },
    onQtyChanged(pIdx, mIdx, e) {
      const product = this.productRows[pIdx];
      const machineRow = product.machine_rows[mIdx];
      this.$set(product.machine_rows, mIdx, {
        ...machineRow,
        quantity: parseFloat(e.value) || 0,
      });
      this.globalError = null;
    },
    closeWith(result) {
      this.visible = false;
      if (this.promiseResolve) {
        this.promiseResolve(result);
      }
      this.promiseResolve = null;
    },
    onHidden() {
      if (this.promiseResolve) {
        this.promiseResolve(null);
        this.promiseResolve = null;
      }
      this.productRows = [];
      this.globalError = null;
    },
    onCancel() {
      this.closeWith(null);
    },
    onConfirm() {
      if (!this.allSumsOk) {
        this.globalError = this.$t("stock_mobile.form.error_machine_split_sum_must_match");
        return;
      }

      const result = this.productRows.map((product) => ({
        product_uuid: product.product_uuid,
        machine_splits: product.machine_rows.map((row) => ({
          work_machine_uuid: row.work_machine_uuid,
          quantity: row.quantity,
        })),
      }));

      this.closeWith(result);
    },
  },
};
</script>

<style lang="scss" scoped>
.machine-split-popup-wrapper {
  :deep(.dx-popup-content) {
    padding: 0 !important;
  }
}

.machine-split-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 20px 24px 16px;

  .machine-split-back {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .machine-split-title {
    font-weight: bold;
    color: #ea652a;
  }
}

.machine-split-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.machine-split-scrollview {
  flex: 1;
}

.machine-split-products {
  padding: 12px 24px 0;
}

.machine-split-product {
  margin-bottom: 12px;
}

.machine-split-product-header {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  margin-bottom: 4px;
}

.machine-split-sum-indicator {
  font-size: 13px;
  margin-bottom: 8px;

  &.machine-split-sum-ok {
    color: #1a8f3d;
  }

  &.machine-split-sum-error {
    color: #dc3545;
  }
}

.machine-split-machine-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .machine-split-machine-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .machine-split-machine-unit {
    width: 40px;
  }
}

.machine-split-global-error {
  color: #dc3545;
  padding: 0 24px 8px;
}

.machine-split-actions {
  padding: 12px 24px 24px;
}

.machine-split-btn-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.machine-split-bottom-spacer {
  height: 24px;
}
</style>
